const RESULTS_PREFIX = '/results';
const ACCURACY_DRILLDOWN_PREFIX = '/accuracy/drilldown';

/** Allowed in-app return targets for the annotation page (path + query). */
const ANNOTATION_RETURN_PREFIXES = [RESULTS_PREFIX, ACCURACY_DRILLDOWN_PREFIX] as const;

function isSafePathWithOptionalQuery(pathAndQuery: string, prefix: string): boolean {
    return pathAndQuery === prefix || pathAndQuery.startsWith(`${prefix}?`);
}

function isSafeInAppPathAndQuery(pathAndQuery: string): boolean {
    if (typeof pathAndQuery !== 'string') return false;
    const s = pathAndQuery.trim();
    if (s.length === 0 || s.length > 4096) return false;
    if (s.startsWith('//')) return false;
    return ANNOTATION_RETURN_PREFIXES.some((prefix) => isSafePathWithOptionalQuery(s, prefix));
}

/**
 * Allow only in-app results list URLs (path + query), to avoid open redirects.
 * Accepts `/results` or `/results?...`.
 */
export function isSafeResultsListReturnTo(pathAndQuery: string): boolean {
    if (typeof pathAndQuery !== 'string') return false;
    const s = pathAndQuery.trim();
    if (s.length === 0 || s.length > 4096) return false;
    if (s.startsWith('//')) return false;
    return isSafePathWithOptionalQuery(s, RESULTS_PREFIX);
}

export function decodeSafeReturnTo(encoded: string | null | undefined): string | null {
    if (encoded == null || encoded === '') return null;
    try {
        const decoded = decodeURIComponent(encoded.replace(/\+/g, ' '));
        return isSafeInAppPathAndQuery(decoded) ? decoded : null;
    } catch {
        return null;
    }
}

export function decodeSafeResultsListReturnTo(encoded: string | null | undefined): string | null {
    if (encoded == null || encoded === '') return null;
    try {
        const decoded = decodeURIComponent(encoded.replace(/\+/g, ' '));
        return isSafeResultsListReturnTo(decoded) ? decoded : null;
    } catch {
        return null;
    }
}

export function getAnnotationBackLabel(returnToPathAndQuery: string): string {
    const path = returnToPathAndQuery.split('?')[0];
    if (path === ACCURACY_DRILLDOWN_PREFIX) return '件数内訳';
    if (path === RESULTS_PREFIX) return 'ジョブのURL一覧';
    return '戻る';
}

export function resolveAnnotationBackNavigation(params: {
    returnToRaw: string | null;
    jobId?: string;
}): { href: string; label: string } {
    const fromQuery = decodeSafeReturnTo(params.returnToRaw);
    if (fromQuery) {
        return { href: fromQuery, label: getAnnotationBackLabel(fromQuery) };
    }
    if (params.jobId) {
        return {
            href: `/results?jobId=${encodeURIComponent(params.jobId)}`,
            label: 'ジョブのURL一覧',
        };
    }
    return { href: '/results', label: 'ジョブのURL一覧' };
}

/** Link target for the annotation page, including optional `returnTo` query. */
export function buildViolationAnnotationTo(
    jobId: string,
    urlId: string,
    resultsPathnameAndSearch: string
): string {
    const jid = encodeURIComponent(jobId);
    const uid = encodeURIComponent(urlId);
    const base = `/violation/${jid}/${uid}`;
    if (!isSafeResultsListReturnTo(resultsPathnameAndSearch)) {
        return base;
    }
    return `${base}?returnTo=${encodeURIComponent(resultsPathnameAndSearch)}`;
}
